import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { api, customerWaLink } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function MonthlyReports() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/agent-reports").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const markSent = async (uid) => {
    await api.post(`/admin/agent-reports/${uid}/sent`);
    toast.success("Report marked as sent");
    load();
  };
  const pending = data.reports.filter((r) => !r.sent).length;

  return (
    <section data-testid="monthly-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl font-bold text-ink flex items-center gap-2"><CalendarCheck size={20} className="text-gold" /> Monthly reports · {data.month}
          <span className="text-ink/40 text-base font-normal">({pending} to send)</span></h2>
        {data.is_report_window && pending > 0 && <span className="text-[11px] font-semibold bg-gold/20 text-ink px-3 py-1 rounded-full" data-testid="monthly-reports-ready">Reports ready — tap to WhatsApp each agent</span>}
      </div>
      {data.reports.length === 0 ? <p className="text-sm text-ink/50 mt-3" data-testid="monthly-reports-empty">No agent activity last month.</p> : (
        <div className="grid grid-cols-1 gap-3 mt-4">
          {data.reports.map((r) => (
            <div key={r.user_id} className={`bg-white border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3 ${r.sent ? "border-emerald-200" : "border-ink/10"}`} data-testid={`report-row-${r.user_id.slice(5, 13)}`}>
              <div className="min-w-0">
                <p className="font-semibold text-ink truncate">{r.rank && <span className="font-mono text-xs text-terra mr-2">#{r.rank}</span>}{r.name} <span className="text-ink/40 font-normal">· {r.email}</span></p>
                <p className="text-xs text-ink/60 mt-1">{r.bookings} booking{r.bookings === 1 ? "" : "s"} · {inr(r.earned)} earned · {r.clicks} link opens{!r.phone && " · no WhatsApp number yet"}</p>
                {r.sent && <p className="text-[11px] text-emerald-700 mt-1" data-testid={`report-sent-${r.user_id.slice(5, 13)}`}>Sent via {r.sent.channel} · {new Date(r.sent.sent_at).toLocaleDateString("en-IN")}</p>}
              </div>
              <div className="flex gap-2">
                {r.phone && (
                  <a href={customerWaLink(r.phone, r.message)} target="_blank" rel="noreferrer" onClick={() => !r.sent && markSent(r.user_id)} data-testid={`report-whatsapp-${r.user_id.slice(5, 13)}`}
                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold px-4 py-2 rounded-full hover:brightness-110"><MessageCircle size={13} /> {r.sent ? "Send again" : "Send on WhatsApp"}</a>
                )}
                {!r.sent && <button onClick={() => markSent(r.user_id)} data-testid={`report-mark-sent-${r.user_id.slice(5, 13)}`} className="inline-flex items-center gap-1 border border-ink/15 text-ink/60 text-xs font-semibold px-3 py-2 rounded-full hover:bg-sand"><Check size={12} /> Mark sent</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
